import React, { useState, useEffect } from 'react';
import { adminAPI } from '../../../services/api';
import { FiPlus, FiEdit, FiTrash2 } from 'react-icons/fi';
import '../Students/StudentsPage.css';

const AreasPage = () => {
    const [areas, setAreas] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [showModal, setShowModal] = useState(false);
    const [editingArea, setEditingArea] = useState(null);

    const [formData, setFormData] = useState({
        name: '',
        category: 'grammar',
        description: '',
        sortOrder: 0
    });

    useEffect(() => {
        loadAreas();
    }, []);

    const loadAreas = async () => {
        try {
            setLoading(true);
            const response = await adminAPI.getAreas();
            setAreas(response.data.areas);
            setError('');
        } catch (err) {
            setError('Ошибка при загрузке областей обучения');
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const handleInputChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        try {
            if (editingArea) {
                await adminAPI.updateArea(editingArea.id, formData);
            } else {
                await adminAPI.createArea(formData);
            }
            
            setShowModal(false);
            setEditingArea(null);
            resetForm();
            loadAreas();
        } catch (err) {
            setError(err.response?.data?.error || 'Ошибка при сохранении области');
        }
    };

    const handleEdit = (area) => {
        setEditingArea(area);
        setFormData({
            name: area.name,
            category: area.category || 'grammar',
            description: area.description || '',
            sortOrder: area.sort_order || 0
        });
        setShowModal(true);
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Вы уверены, что хотите удалить эту область? Все связанные уровни и уроки также будут удалены!')) {
            return;
        }

        try {
            await adminAPI.deleteArea(id);
            loadAreas();
        } catch (err) {
            setError('Ошибка при удалении области');
        }
    };

    const resetForm = () => {
        setFormData({
            name: '',
            category: 'grammar',
            description: '',
            sortOrder: 0
        });
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setEditingArea(null);
        resetForm();
    };

    if (loading) {
        return <div className="loading">Загрузка...</div>;
    }

    return (
        <div className="container">
            <div className="page-header">
                <div>
                    <h1>Области обучения</h1>
                    <p>Управление областями (General English, ЕГЭ, Бизнес-английский и т.д.)</p>
                </div>
                <button 
                    className="btn btn-primary"
                    onClick={() => setShowModal(true)}
                >
                    <FiPlus /> Добавить область
                </button>
            </div>

            {error && <div className="error-message">{error}</div>}

            <div className="card">
                <div className="students-table">
                    <table>
                        <thead>
                            <tr>
                                <th>Порядок</th>
                                <th>Название</th>
                                <th>Категория</th>
                                <th>Описание</th>
                                <th>Статус</th>
                                <th>Дата создания</th>
                                <th>Действия</th>
                            </tr>
                        </thead>
                        <tbody>
                            {areas.length === 0 ? (
                                <tr>
                                    <td colSpan="7" style={{ textAlign: 'center' }}>
                                        Нет областей обучения. Создайте первую область.
                                    </td>
                                </tr>
                            ) : (
                                areas.map((area) => (
                                    <tr key={area.id}>
                                        <td>{area.sort_order}</td>
                                        <td><strong>{area.name}</strong></td>
                                        <td>
                                            <span style={{
                                                padding: '4px 12px',
                                                borderRadius: '12px',
                                                fontSize: '12px',
                                                fontWeight: '600',
                                                background: area.category === 'grammar' ? '#e3f2fd' : '#fff3e0',
                                                color: area.category === 'grammar' ? '#1976d2' : '#f57c00'
                                            }}>
                                                {area.category === 'grammar' ? 'Грамматика' : 'Лексика'}
                                            </span>
                                        </td>
                                        <td>{area.description || '—'}</td>
                                        <td>
                                            <span className={`status-badge ${area.is_active ? 'active' : 'inactive'}`}>
                                                {area.is_active ? 'Активна' : 'Неактивна'}
                                            </span>
                                        </td>
                                        <td>
                                            {new Date(area.created_at).toLocaleDateString('ru-RU')}
                                        </td>
                                        <td>
                                            <div className="table-actions">
                                                <button
                                                    className="btn-icon"
                                                    onClick={() => handleEdit(area)}
                                                    title="Редактировать"
                                                >
                                                    <FiEdit />
                                                </button>
                                                <button
                                                    className="btn-icon btn-danger"
                                                    onClick={() => handleDelete(area.id)}
                                                    title="Удалить"
                                                >
                                                    <FiTrash2 />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {showModal && (
                <div className="modal-overlay" onClick={handleCloseModal}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h2>{editingArea ? 'Редактировать область' : 'Добавить область'}</h2>
                            <button onClick={handleCloseModal} className="modal-close">×</button>
                        </div>

                        <form onSubmit={handleSubmit} className="modal-form">
                            <div className="form-group">
                                <label>Название *</label>
                                <input
                                    type="text"
                                    name="name"
                                    value={formData.name}
                                    onChange={handleInputChange}
                                    placeholder="Например: General English"
                                    required
                                />
                            </div>

                            <div className="form-group">
                                <label>Категория *</label>
                                <select
                                    name="category"
                                    value={formData.category}
                                    onChange={handleInputChange}
                                    required
                                >
                                    <option value="grammar">Грамматика</option>
                                    <option value="vocabulary">Лексика</option>
                                </select>
                            </div>

                            <div className="form-group">
                                <label>Описание</label>
                                <textarea
                                    name="description"
                                    value={formData.description}
                                    onChange={handleInputChange}
                                    placeholder="Краткое описание области обучения"
                                    rows="3"
                                />
                            </div>

                            <div className="form-group">
                                <label>Порядок сортировки</label>
                                <input
                                    type="number"
                                    name="sortOrder"
                                    value={formData.sortOrder}
                                    onChange={handleInputChange}
                                    min="0"
                                />
                            </div>

                            <div className="modal-footer">
                                <button type="button" className="btn" onClick={handleCloseModal}>
                                    Отмена
                                </button>
                                <button type="submit" className="btn btn-primary">
                                    {editingArea ? 'Сохранить' : 'Создать'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            <style>{`
                .status-badge {
                    padding: 4px 12px;
                    border-radius: 12px;
                    font-size: 12px;
                    font-weight: 500;
                }
                .status-badge.active {
                    background-color: #f6ffed;
                    color: #52c41a;
                    border: 1px solid #b7eb8f;
                }
                .status-badge.inactive {
                    background-color: #fff2f0;
                    color: #f5222d;
                    border: 1px solid #ffccc7;
                }
            `}</style>
        </div>
    );
};

export default AreasPage;
